// Payment helper functions for deposit vs full payment processing

/**
 * Convert dollars to cents for Stripe
 * @param {number} dollars - Amount in dollars
 * @returns {number} Amount in cents
 */
export function toCents(dollars) {
  return Math.round(Number(dollars || 0) * 100);
}

/**
 * Convert cents to dollars
 * @param {number} cents - Amount in cents
 * @returns {number} Amount in dollars
 */
export function toDollars(cents) {
  return Number(cents || 0) / 100;
}

/**
 * Round to nearest cent
 * @param {number} amount - Amount to round
 * @returns {number} Rounded amount
 */
export function roundToCents(amount) {
  return Math.round(Number(amount || 0) * 100) / 100;
}

/**
 * Split payment into deposit and balance
 * @param {number} totalAmount - Total amount in dollars
 * @param {number} depositPercent - Deposit percentage (default 50)
 * @returns {object} { deposit, balance, total }
 */
export function splitPayment(total, option = "deposit") {
  const t = Number(total || 0);
  const minDeposit = Math.round(t * 0.5 * 100) / 100;
  if (option === "deposit") {
    return { amountToCharge: minDeposit, balanceDue: Math.round((t - minDeposit) * 100) / 100, option: "deposit" };
  }
  return { amountToCharge: t, balanceDue: 0, option: "full" };
}

/**
 * Calculate payment amounts based on payment plan
 * @param {number} totalAmount - Total amount in dollars
 * @param {string} paymentPlan - 'deposit' or 'full'
 * @param {number} depositPercent - Deposit percentage (default 50)
 * @returns {object} Payment breakdown
 */
export function calculatePayment(totalAmount, paymentPlan = 'deposit', depositPercent = 50) {
  const total = roundToCents(totalAmount);
  
  if (paymentPlan === 'full') {
    return {
      total,
      chargeNow: total,
      balanceDue: 0,
      paymentPlan: 'full'
    };
  }
  
  const { deposit, balance } = splitPayment(total, depositPercent);
  
  return {
    total,
    chargeNow: deposit,
    balanceDue: balance,
    paymentPlan: 'deposit',
    depositPercent
  };
}

/**
 * Format payment summary for display
 * @param {object} payment - Payment calculation result
 * @returns {object} Formatted payment summary
 */
export function formatPaymentSummary(payment) {
  return {
    total: payment.total,
    chargeNow: payment.chargeNow,
    balanceDue: payment.balanceDue,
    paymentPlan: payment.paymentPlan,
    depositPercent: payment.depositPercent,
    formatted: {
      total: `$${payment.total.toFixed(2)}`,
      chargeNow: `$${payment.chargeNow.toFixed(2)}`,
      balanceDue: `$${payment.balanceDue.toFixed(2)}`
    }
  };
}

/**
 * Validate payment plan
 * @param {string} paymentPlan - Payment plan to validate
 * @returns {boolean} Is valid payment plan
 */
export function isValidPaymentPlan(paymentPlan) {
  return ['deposit', 'full'].includes(paymentPlan);
}

/**
 * Get payment plan description
 * @param {string} paymentPlan - Payment plan
 * @param {number} depositPercent - Deposit percentage
 * @returns {string} Payment plan description
 */
export function getPaymentPlanDescription(paymentPlan, depositPercent = 50) {
  switch (paymentPlan) {
    case 'deposit':
      return `${depositPercent}% deposit now, balance due before delivery`;
    case 'full':
      return 'Full payment now';
    default:
      return 'Select payment option';
  }
}
